<template>
  <div
    :id="clickable ? buttonId : undefined"
    class="overflow-hidden rounded-3xl border border-solid border-gray-500 bg-white transition-shadow tablet:flex"
    :class="[
      clickable &&
        'group cursor-pointer hover:shadow-card hover:shadow-blue-400_a3',
      hasShadow && 'shadow-card',
    ]"
    :title="clickable ? title : ''"
    @click="handleClick"
  >
    <div
      class="flex shrink-0 flex-col justify-between gap-2 p-4 tablet:w-1/3 tablet:p-5"
    >
      <div>
        <h3 class="text-h3 mb-2 truncate">
          {{ title }}
        </h3>

        <p class="w-full text-sm leading-6 text-gray-700 tablet:line-clamp-7">
          {{ description }}
        </p>
      </div>

      <TheNuxtLink
        v-if="buttonName && buttonUrl"
        :id="!clickable ? buttonId : undefined"
        ref="linkRef"
        :to="buttonUrl"
        :title="!clickable ? buttonName : ''"
        :class="!clickable && 'hover:underline'"
        class="mt-auto inline-flex w-fit items-center truncate text-sm font-bold leading-6 text-blue-400 tablet:text-base"
      >
        {{ buttonName }}
      </TheNuxtLink>
    </div>

    <TheMedia
      :title="title"
      :description="description"
      :video-data="videoData"
      :thumbnail="imageUrl"
      :has-arrow-button="true"
      :theme="theme"
      class="aspect-video w-full tablet:aspect-[4/3] tablet:w-2/3 desktop:aspect-video"
    />
  </div>
</template>

<script setup lang="ts">
  import type { RouteLocationRaw } from 'vue-router';

  import type { IVideoData } from '@/components/Shared/Lightbox/Video.vue';
  import TheNuxtLink from '@/components/TheNuxtLink.vue';

  interface IProps {
    title: string;
    description: string;
    buttonId?: string;
    buttonUrl?: RouteLocationRaw;
    buttonName?: string;
    imageUrl: string;
    videoUrl?: string;
    clickable?: boolean;
    hasShadow?: boolean;
    theme?: TTheme;
  }

  const props = withDefaults(defineProps<IProps>(), {
    videoUrl: undefined,
    buttonId: undefined,
    buttonUrl: undefined,
    buttonName: undefined,
    clickable: false,
    hasShadow: true,
    theme: 'blue',
  });

  const videoData = computed((): IVideoData | undefined =>
    props.videoUrl
      ? {
          title: props.title,
          description: props.description,
          url: props.videoUrl,
          imageUrl: props.imageUrl,
          buttonPosition: 'center',
        }
      : undefined
  );

  const linkRef = ref<InstanceType<typeof TheNuxtLink> | null>(null);

  const handleClick = (event: MouseEvent): void => {
    const linkEl = linkRef.value?.$el as HTMLElement | undefined;
    const isOutsideLink = linkEl && !linkEl.contains(event.target as Node);
    // 避免點擊連結本身時再次觸發，造成無窮迴圈
    if (props.clickable && props.buttonUrl && isOutsideLink) {
      linkEl.click();
    }
  };
</script>
